import { METHOD_TYPES } from './constants';

const PASSKEY_TYPES = METHOD_TYPES.find(({ key }) => key === 'passkey').types;
const NON_PASSKEY_METHOD_TYPES = METHOD_TYPES.filter(({ key }) => key !== 'passkey');

// Message ids for why a method can't be removed, keyed by customers-api's deletion guardrail
// errorCode, so a 409 refusal and the upfront check show the same copy.
export const REMOVAL_BLOCKED_MESSAGES = {
  wouldLeaveNoSms: 'dashboard.accountSettings.twoStepVerification.removalBlocked.wouldLeaveNoSms',
  wouldLeaveOnlyWebauthn: 'dashboard.accountSettings.twoStepVerification.removalBlocked.wouldLeaveOnlyWebauthn',
};

/**
 * Mirrors customers-api's deletion guardrails so the card can explain a refusal before the
 * user tries.
 *
 * @param {object} method - The method the user wants to remove.
 * @param {object[]} confirmedMethods - All of the user's confirmed methods, including recovery codes and
 *   types without a row, since customers-api counts them as remaining methods too.
 * @param {boolean} requireMfa - Whether the account requires MFA (banking).
 * @returns {{ id: string, values: object }|null} The message of why the method can't be removed, or null when it can.
 */
export function getRemovalBlockedReason(method, confirmedMethods, requireMfa) {
  const remaining = confirmedMethods.filter(({ id }) => id !== method.id);

  // customers-api only counts an SMS factor, not one set to voice, as the phone a banking account needs.
  if (requireMfa && method.type === 'sms' && !remaining.some(({ type }) => type === 'sms')) {
    return { id: REMOVAL_BLOCKED_MESSAGES.wouldLeaveNoSms, values: {} };
  }

  // Auth0 lets someone with only the password enroll a new factor when WebAuthn is the only one left,
  // so a passkey alone doesn't protect the account. A remaining recovery code still forces a challenge,
  // and removing a passkey never makes things worse, so neither is blocked.
  if (
    !PASSKEY_TYPES.includes(method.type) &&
    remaining.length > 0 &&
    remaining.every(({ type }) => PASSKEY_TYPES.includes(type))
  ) {
    // The method being removed is the only non-passkey one, so the one to add is the type the user doesn't have.
    const missing = NON_PASSKEY_METHOD_TYPES.find(
      ({ types }) => !confirmedMethods.some(({ type }) => types.includes(type)),
    );

    return { id: REMOVAL_BLOCKED_MESSAGES.wouldLeaveOnlyWebauthn, values: { missing: missing?.key || 'other' } };
  }

  return null;
}
